import { useCreateEvaluationReport } from "@/api/console/evaluation-reports/useCreateEvaluationReport";
import { useSaveEvaluationReport } from "@/api/console/evaluation-reports/useSaveEvaluationReport";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/config/progress";
import toast from "@/lib/toast";
import { EvaluationReport } from "@/schema/console/evaluation-reports/EvaluationReport";
import { EvaluationReportCreate } from "@/schema/console/evaluation-reports/EvaluationReportCreate";
import AssessmentResultDialog from "@/src/console/(auth)/_components/AssessmentResultDialog/AssessmentResultDialog";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";

interface ButtonGroupProps {
  id: string;
  evaluationReport: EvaluationReport;
}

function ButtonGroup({ id, evaluationReport }: ButtonGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const methods = useFormContext<EvaluationReport>();

  const { trigger: saveEvaluationReport, isMutating: isSaving } =
    useSaveEvaluationReport(id);

  const { trigger: createEvaluationReport, isMutating: isCreating } =
    useCreateEvaluationReport(id, {
      onSuccess: (data) => {
        toast.success(data.message);
        const statuses = [
          STATUS.SUBMITTED,
          STATUS.ASSESSMENT_GRADED_COMPLETED,
          STATUS.ASSESSMENT_EVALUATION_COMPLETED,
          STATUS.IMPROVEMENT_PROPOSAL_COMPLETED,
        ];
        mutate(`/console/assessment_progresses?status=${statuses.join(",")}`);
        navigate(`/console/check-submissions`);
      },
      revalidate: false,
    });

  const prepareData = (data: EvaluationReport): EvaluationReportCreate => {
    const {
      result_index: { main_category_results, sdg_goal_results },
    } = data;

    const subCategoryResults = main_category_results.flatMap((mainCategory) => {
      return mainCategory.sub_category_results.map(
        ({ id, evaluation_note }) => ({
          id,
          evaluation_note: evaluation_note ?? "",
        }),
      );
    });

    const sdgTargetResults = sdg_goal_results.flatMap((goal) => {
      return goal.sdg_target_results.map(({ id, evaluation_note }) => ({
        id,
        evaluation_note: evaluation_note ?? "",
      }));
    });

    return {
      sub_category_results: subCategoryResults,
      sdg_target_results: sdgTargetResults,
    };
  };

  return (
    <div className=" flex  gap-4">
      <AssessmentResultDialog evaluationReport={evaluationReport} />
      <Button
        isLoading={isSaving}
        variant="outline"
        onClick={() => {
          methods.handleSubmit((data) => {
            toast.loading(t("save-draft-loading"));
            saveEvaluationReport(prepareData(data));
          })();
        }}
      >
        {t("save-draft")}
      </Button>
      <Button
        isLoading={isCreating}
        onClick={() => {
          methods.handleSubmit((data) => {
            toast.loading(t("submit-report-loading"));
            createEvaluationReport(prepareData(data));
          })();
        }}
      >
        {t("confirm-and-save-report")}
      </Button>
    </div>
  );
}

export default ButtonGroup;
